import BaseService from '../base-service';
import NetworkUrls from '../network-urls';


class ClientService extends BaseService {
  async login(email, password) {
    const config = {
      url: NetworkUrls.login,
      method: 'POST',
      data: {
        email,
        password,
      },
    };

    return this._exec(config, false);
  }

  async refresh(accessToken, refreshToken, onError = () => {}) {
    const config = {
      url: NetworkUrls.refresh,
      method: 'POST',
      data: {
        access_token: accessToken,
        refresh_token: refreshToken,
      },
      errorHandler: onError
    };

    return this._exec(config, false);
  }

  async getName(accessToken) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `{
            Auth {
                id,
                firstName,
                lastName,
                phone,
                title,
                email,
                type
            }
        }`,
      },
    };
    return this._exec(config);
  }

  async getProfile(accessToken) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `{
            Auth {
                id,
                firstName,
                lastName,
                phone,
            }
        }`,
      },
    };
    return this._exec(config);
  }

  async getAddresses(accessToken) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `{
            Auth {
              addresses {
                id,
                is_main,
                society_name,
                address,
                additional,
                description,
                city,
                zip,
                country {
                  id,
                  alpha2
                }
              }
            }
        }`,
      },
    };
    return this._exec(config);
  }

  async updateAddress(accessToken, params) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `mutation ($address_id: CID!, $description: String, $address: String, $additional: String, $city: String, $zip: String, $country_id: CID, $is_main: Boolean, $society_name: String) {
            Address {
                updateAddress (id: $address_id, description: $description, address: $address, additional: $additional, city: $city, zip: $zip, country_id: $country_id, is_main: $is_main, society_name: $society_name) {
                    updated,
                    updatedRowCount,
                    id
                }
            }
        }`,
        variables: params,
      },
    };
    return this._exec(config);
  }

  async updateProfile(accessToken, { firstName, lastName, phone, password, securePwd }) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `mutation ($firstName: String!, $lastName: String!, $phone: PhoneE164!, $password: String!, $securePwd: String!) {
            Auth {
                update (firstName: $firstName, lastName: $lastName, phone: $phone, password: $password, securePwd: $securePwd) {
                    updated,
                    updatedRowCount,
                    id
                }
            }
        }`,
        variables: { firstName, lastName, phone, password, securePwd },
      },
    };
    return this._exec(config);
  }

  async deleteAddress(accessToken, address_id) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `mutation ($id: CID!) {
            Address {
                deleteAddress (id: $id) {
                    deleted,
                    updatedRowCount,
                    id
                }
            }
        }`,
        variables: {
          id: address_id,
        },
      },
    };
    return this._exec(config);
  }

  async createAddress(accessToken, { description, address, additional, city, zip, country_id, is_main, society_name }) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `mutation ($description: String!, $address: String!, $additional: String, $city: String!, $zip: String!, $country_id: CID!, $is_main: Boolean!, $society_name: String) {
            Address {
                createAddress (description: $description, address: $address, additional: $additional, city: $city, zip: $zip, country_id: $country_id, is_main: $is_main, society_name: $society_name) {
                    inserted,
                    insertedRowCount,
                    id
                }
            }
        }`,
        variables: {
          description,
          address,
          additional,
          city,
          zip,
          country_id,
          is_main,
          society_name: society_name === '' ? null : society_name
        },
      },
    };
    return this._exec(config);
  }

  async createSetupIntent(accessToken) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `mutation {
          Auth {
            sources {
              createSetupIntent {
                client_secret
              }
            }
          }
        }`,
      },
    };
    return this._exec(config);
  }

  async attachSetupIntent(accessToken, paymentMethod) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `mutation ($paymentMethod: String!) {
          Auth {
            sources {
              attachPaymentMethod (paymentMethod: $paymentMethod) {
                attached
              }
            }
          }
        }`,
        variables: { paymentMethod },
      },
    };
    return this._exec(config);
  }

  async fetchCards(accessToken) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `query {
          Auth {
            sources {
              id,
              is_default,
              billing_details {
                name
              },
              card {
                brand,
                country,
                exp_month,
                exp_year,
                last4
              }
            }
          }
        }`,
      },
    };
    return this._exec(config);
  }

  async deleteCard(accessToken, cardId) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `mutation ($cardId: String!) {
          Auth {
            sources {
              deleteCard (id: $cardId) {
                deleted
              }
            }
          }
        }`,
        variables: { cardId },
      },
    };
    return this._exec(config);
  }

  async setMain(accessToken, cardId) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      headers: {
        Authorization: accessToken,
      },
      data: {
        query: `mutation ($cardId: String!) {
          Auth {
            sources {
              setMain (id: $cardId) {
                updated
              }
            }
          }
        }`,
        variables: { cardId },
      },
    };
    return this._exec(config);
  }

  async createAccount (data) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      data: {
        query: `mutation ($registeredFrom: ClientSourceEnum, $title: GenderEnum!, $firstName: String!, $lastName: String!, $phone: PhoneE164!, $email: EmailType!, $password: String!, $dobTimestamp: Int!, $country: CID!, $pub: Boolean, $address: AddressInputType!, $stayReason: StayReasonEnum!) {
                  Auth {
                      register (registeredFrom: $registeredFrom, title: $title, firstName: $firstName, lastName: $lastName, phone: $phone, email: $email, password: $password, dobTimestamp: $dobTimestamp, country: $country, pub: $pub, address: $address, stayReason: $stayReason) {
                          inserted,
                          insertedRowCount,
                          id
                      }
                  }
              }`,
        variables: data
      },
    };
    return this._exec(config);
  }

  async sendForgotRequest (email) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      data: {
        query: `mutation ($email: EmailType!) {
          Auth {
            resetPassword (email: $email) {
              success
            }
          }
        }`,
        variables: {email}
      }
    };
    return this._exec(config);
  }

  async changePasswordWithCode ({email, password, code}) {
    const config = {
      url: NetworkUrls.graphql,
      method: 'POST',
      data: {
        query: `mutation ($password: String!, $code: String!, $email: EmailType!) {
          Auth {
            setNewPassword (password: $password, code: $code, email: $email) {
              success
            }
          }
        }`,
        variables: {email, password, code}
      }
    };
    return this._exec(config);
  }

}

export const clientService = new ClientService();



// WEBPACK FOOTER //
// ./src/network/services/client.service.js